import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * The GUEST cart, and only the guest cart.
 *
 * Once you are signed in the cart lives in Mongo and TanStack Query owns it
 * (`keys.cart`) — that is what makes it survive a device change. This store is
 * the pre-login half, and on login its lines are POSTed to /cart/merge and then
 * dropped.
 *
 * It stores identifiers and quantities. It deliberately does NOT store prices,
 * discounts or totals: every figure the customer sees comes from the server's
 * pricing response, so a tampered localStorage can change what you are asking to
 * buy but never what it costs. A secondhand line is also capped server-side to
 * the copy that actually exists — usually exactly one.
 */
const MAX_LINES = 50;

export const useCartStore = create(
  persist(
    (set, get) => ({
      /** [{ kind: 'secondhand' | 'publisher', bookId, slug, qty }] */
      lines: [],

      addLine({
        kind,
        bookId,
        slug,
        qty = 1,
        title,
        authors,
        author,
        coverUrl,
        unitPriceMinor,
        priceMinor,
        stock,
        condition,
      }) {
        const lines = get().lines;
        const existing = lines.find((l) => l.bookId === bookId);

        if (existing) {
          set({
            lines: lines.map((l) =>
              l.bookId === bookId
                ? { ...l, qty: l.qty + qty, quantity: l.qty + qty }
                : l,
            ),
          });
          return;
        }

        if (lines.length >= MAX_LINES) return;
        set({
          lines: [
            ...lines,
            {
              id: bookId,
              kind,
              bookId,
              slug,
              qty,
              quantity: qty,
              title: title || '',
              authors: authors || (author ? [author] : []),
              coverUrl: coverUrl || null,
              unitPriceMinor: unitPriceMinor ?? priceMinor ?? 0,
              stock: stock || 1,
              condition: condition || null,
            },
          ],
        });
      },

      setQty(bookId, qty) {
        if (qty <= 0) {
          get().removeLine(bookId);
          return;
        }
        set({
          lines: get().lines.map((l) =>
            l.bookId === bookId ? { ...l, qty, quantity: qty } : l,
          ),
        });
      },

      removeLine(bookId) {
        set({ lines: get().lines.filter((l) => l.bookId !== bookId && l.id !== bookId) });
      },

      clear() {
        set({ lines: [] });
      },
    }),
    {
      name: 'chapterco.guest-cart',
      version: 1,
      partialize: (state) => ({ lines: state.lines }),
    },
  ),
);

/** Header badge count. Not a total — see the note above. */
export const selectCartCount = (s) => s.lines.reduce((n, l) => n + l.qty, 0);

export default useCartStore;
